<script setup>
import { ref, onMounted, onUnmounted, useTemplateRef } from 'vue';
import WinkerSsoAPI from 'dashboard/api/winkerSso';
import ArticleSkeletonLoader from 'shared/components/ArticleSkeletonLoader.vue';

const props = defineProps({
  url: {
    type: String,
    default: '',
  },
});

const iframe = useTemplateRef('iframe');
const isLoading = ref(true);

const getTargetOrigin = () => {
  try {
    return new URL(props.url).origin;
  } catch {
    return '*';
  }
};

// Minta o token de handshake uma vez e reusa enquanto fresco (TTL 60s no backend). O load do
// iframe e o pedido 'fetch-info' disparam o appContext quase juntos; sem isso, cada um mintava um
// token (2x winker_sso). Cacheamos a PROMISE p/ deduplicar também chamadas concorrentes.
let embedTokenPromise = null;
let embedTokenAt = 0;
const TOKEN_REUSE_MS = 45000;

const getEmbedToken = () => {
  if (embedTokenPromise && Date.now() - embedTokenAt < TOKEN_REUSE_MS) {
    return embedTokenPromise;
  }

  embedTokenAt = Date.now();
  embedTokenPromise = WinkerSsoAPI.create()
    .then(({ data: tokenData }) => tokenData?.token ?? null)
    .catch(() => {
      embedTokenPromise = null; // libera nova tentativa em caso de falha
      return null;
    });

  return embedTokenPromise;
};

const sendAppContext = async () => {
  if (!iframe.value?.contentWindow) return;

  const data = {};
  const embedToken = await getEmbedToken();
  if (embedToken) data.embedToken = embedToken;

  iframe.value.contentWindow.postMessage(
    JSON.stringify({ event: 'appContext', data }),
    getTargetOrigin()
  );
};

const handleMessage = event => {
  if (event.data === 'chatwoot-dashboard-app:fetch-info') {
    sendAppContext();
  }
};

onMounted(() => window.addEventListener('message', handleMessage));
onUnmounted(() => window.removeEventListener('message', handleMessage));

const handleLoad = () => {
  isLoading.value = false;
  sendAppContext();
};
</script>

<template>
  <div class="relative overflow-hidden h-full">
    <iframe
      v-if="url"
      ref="iframe"
      :src="url"
      class="absolute w-full h-full top-0 left-0 border-0"
      @load="handleLoad"
    />
    <ArticleSkeletonLoader
      v-if="isLoading"
      class="absolute w-full h-full top-0 left-0"
    />
  </div>
</template>
